<template>
    <div class="bg-color rounded-5 p-5">
        <div class="w-100 rounded-4">
            <div
                class="d-flex justify-content-between align-items-center"
                style="background: rgb(241, 247, 255); border-radius: 27px 27px 0 0"
            >
                <h1 class="px-5 py-3 page-heading">Notification Templates</h1>
            </div>

            <div v-if="loading" class="w-100 bg-white loading-panel">
                <div class="d-grid justify-content-center align-items-center py-5" style="min-height: 400px">
                    <h4>Loading notification templates...</h4>
                </div>
            </div>

            <div v-else-if="!applications.length" class="w-100 bg-white empty-state">
                <span class="material-symbols-outlined empty-state-icon">notifications_off</span>
                <h4>No applications configured</h4>
                <p>Select an Application Builder application to configure its notification templates.</p>
                <button type="button" class="rail-add-btn empty-add-btn" @click="showAddModal = true">
                    <span class="material-symbols-outlined rail-add-icon">add</span>
                    Add Application
                </button>
            </div>

            <div v-else class="content-grid bg-white">
                <aside class="app-rail">
                    <div class="rail-heading">
                        Applications
                        <span class="rail-count">{{ applications.length }}</span>
                    </div>
                    <div class="rail-list">
                        <button
                            v-for="application in applications"
                            :key="application.id"
                            type="button"
                            class="app-rail-item"
                            :class="{ active: selectedApp && application.id === selectedApp.id }"
                            @click="selectApp(application)"
                        >
                            <span class="app-rail-avatar">{{ appInitials(application.name) }}</span>
                            <span class="app-rail-name">{{ application.name }}</span>
                            <span
                                v-if="dirtyByAppId[application.id]"
                                class="dirty-dot"
                                title="Unsaved changes"
                            ></span>
                        </button>
                    </div>
                    <button type="button" class="rail-add-btn" @click="showAddModal = true">
                        <span class="material-symbols-outlined rail-add-icon">add</span>
                        Add Application
                    </button>
                </aside>

                <main v-if="selectedApp" class="template-panel">
                    <div class="panel-intro">
                        <h2 class="app-title">{{ selectedApp.name }}</h2>
                        <p class="app-subtitle">
                            <span class="material-symbols-outlined subtitle-icon">info</span>
                            These templates are used for real member messages — changes take effect immediately after
                            saving.
                        </p>
                    </div>

                    <section v-for="group in flowGroups" :key="group.key" class="flow-card">
                        <div class="flow-header">
                            <h3 class="flow-title">{{ group.label }}</h3>
                            <span class="flow-description">{{ group.description }}</span>
                        </div>

                        <div class="channel-tabs" role="tablist" :aria-label="group.label + ' channel'">
                            <button
                                type="button"
                                role="tab"
                                class="channel-tab"
                                :class="{ active: activeChannel[group.key] === 'email' }"
                                :aria-selected="activeChannel[group.key] === 'email' ? 'true' : 'false'"
                                @click="setChannel(group.key, 'email')"
                            >
                                <span class="material-symbols-outlined channel-tab-icon">mail</span>
                                Email
                                <span
                                    v-if="isFieldDirty(group.emailField)"
                                    class="dirty-dot"
                                    title="Unsaved changes"
                                ></span>
                                <span
                                    v-else-if="!isFieldConfigured(group.emailField)"
                                    class="unset-chip"
                                    title="No template configured yet"
                                    >Not set</span
                                >
                            </button>
                            <button
                                type="button"
                                role="tab"
                                class="channel-tab"
                                :class="{ active: activeChannel[group.key] === 'sms' }"
                                :aria-selected="activeChannel[group.key] === 'sms' ? 'true' : 'false'"
                                @click="setChannel(group.key, 'sms')"
                            >
                                <span class="material-symbols-outlined channel-tab-icon">sms</span>
                                SMS
                                <span
                                    v-if="isFieldDirty(group.smsField)"
                                    class="dirty-dot"
                                    title="Unsaved changes"
                                ></span>
                                <span
                                    v-else-if="!isFieldConfigured(group.smsField)"
                                    class="unset-chip"
                                    title="No template configured yet"
                                    >Not set</span
                                >
                            </button>
                        </div>

                        <TemplateFieldEditor
                            v-if="activeChannel[group.key] === 'email'"
                            :key="selectedApp.id + '-' + group.emailField"
                            v-model="workingTemplates[group.emailField]"
                            channel="email"
                            :placeholders="placeholders[group.emailField]"
                            :sample-values="sampleValues"
                        />
                        <TemplateFieldEditor
                            v-else
                            :key="selectedApp.id + '-' + group.smsField"
                            v-model="workingTemplates[group.smsField]"
                            channel="sms"
                            :placeholders="placeholders[group.smsField]"
                            :sample-values="sampleValues"
                        />
                    </section>

                    <div class="d-flex justify-content-end align-items-center save-row">
                        <span v-if="hasBlockingErrors" class="status-pill status-pill-error">
                            <span class="material-symbols-outlined status-pill-icon">error</span>
                            Missing required placeholders
                        </span>
                        <span v-else-if="isDirty" class="status-pill status-pill-warning">
                            <span class="material-symbols-outlined status-pill-icon">edit</span>
                            {{ dirtyFields.length }} unsaved change{{ dirtyFields.length === 1 ? '' : 's' }}
                        </span>
                        <button
                            type="button"
                            class="border-3 save-btn"
                            :class="{ 'btn-disabled': !isDirty || hasBlockingErrors || saving }"
                            :disabled="!isDirty || hasBlockingErrors || saving"
                            :title="saveButtonHint"
                            @click="save"
                        >
                            {{ saving ? 'Saving…' : 'Save Changes' }}
                        </button>
                    </div>
                </main>
            </div>
        </div>

        <ConfirmModal
            :open="discardModalOpen"
            title="Discard unsaved changes?"
            message="You have unsaved template changes that will be lost. Discard them?"
            confirm-label="Discard changes"
            cancel-label="Keep editing"
            danger
            @confirm="handleDiscardConfirm"
            @cancel="handleDiscardCancel"
        />

        <ConfirmModal
            :open="saveModalOpen"
            title="Save template changes?"
            :message="saveModalMessage"
            confirm-label="Save changes"
            @confirm="doSave"
            @cancel="saveModalOpen = false"
        />

        <AddApplicationModal
            :open="showAddModal"
            :applications="applications"
            @close="showAddModal = false"
            @created="handleApplicationCreated"
        />

        <Loading v-if="loading" />
    </div>
</template>

<script>
    import Loading from '@/components/general/loading/loading.vue';
    import TemplateFieldEditor from './TemplateFieldEditor.vue';
    import ConfirmModal from './ConfirmModal.vue';
    import AddApplicationModal from './AddApplicationModal.vue';
    import { applications as applicationsApi } from '@/util/apiRequests';
    import {
        FLOW_GROUPS,
        TEMPLATE_FIELDS,
        PLACEHOLDERS,
        getMissingRequiredTokens,
        getSampleValues,
    } from './notificationTemplates.constants';

    export default {
        name: 'NotificationTemplates',
        components: {
            Loading,
            TemplateFieldEditor,
            ConfirmModal,
            AddApplicationModal,
        },
        data() {
            return {
                loading: true,
                saving: false,
                applications: [],
                selectedApp: null,
                workingTemplates: {},
                originalTemplates: {},
                dirtyByAppId: {},
                showAddModal: false,
                saveModalOpen: false,
                discardModalOpen: false,
                pendingDiscard: null,
                activeChannel: FLOW_GROUPS.reduce((channels, group) => {
                    channels[group.key] = 'email';
                    return channels;
                }, {}),
                flowGroups: FLOW_GROUPS,
                placeholders: PLACEHOLDERS,
            };
        },
        computed: {
            sampleValues() {
                return getSampleValues(this.selectedApp);
            },
            dirtyFields() {
                return TEMPLATE_FIELDS.filter(
                    (field) => (this.workingTemplates[field] || '') !== (this.originalTemplates[field] || '')
                );
            },
            isDirty() {
                return this.dirtyFields.length > 0;
            },
            hasBlockingErrors() {
                // Only edited templates can block saving — pre-existing gaps in untouched
                // templates shouldn't prevent saving unrelated changes
                return this.dirtyFields.some(
                    (field) => getMissingRequiredTokens(field, this.workingTemplates[field]).length > 0
                );
            },
            saveButtonHint() {
                if (this.hasBlockingErrors) {
                    return 'Add the missing required placeholders before saving';
                }
                if (!this.isDirty) {
                    return 'No changes to save';
                }
                return 'Save changes (Ctrl+S)';
            },
            saveModalMessage() {
                const count = this.dirtyFields.length;
                const appName = this.selectedApp ? this.selectedApp.name : '';
                return (
                    `Save ${count} template change${count === 1 ? '' : 's'} for "${appName}"?\n` +
                    'Changes apply to real member messages immediately.'
                );
            },
        },
        watch: {
            isDirty(dirty) {
                if (this.selectedApp) {
                    this.$set(this.dirtyByAppId, this.selectedApp.id, dirty);
                }
            },
        },
        methods: {
            async loadApplications() {
                try {
                    const res = await this.$api.get(applicationsApi.getNotificationTemplates());
                    const data = res.data?.data || {};
                    this.applications = data.applications || [];
                    // Server metadata is authoritative for the legend; local constants stay as fallback
                    this.placeholders = this.normalizePlaceholders(data.placeholders) || PLACEHOLDERS;

                    if (this.applications.length) {
                        this.setWorkingCopy(this.applications[0]);
                    }
                } catch (err) {
                    this.$toasted.error('Failed to load notification templates. Please try again later.');
                } finally {
                    this.loading = false;
                }
            },
            // Server shape: { field: { allowed: [{token, description, legacy?}], required: ['otp'] } }
            // TemplateFieldEditor expects a flat array with a `required` flag per placeholder
            normalizePlaceholders(serverPlaceholders) {
                if (!serverPlaceholders) {
                    return null;
                }
                const normalized = {};
                for (const [field, meta] of Object.entries(serverPlaceholders)) {
                    const required = new Set(meta.required || []);
                    normalized[field] = (meta.allowed || []).map((placeholder) => ({
                        ...placeholder,
                        required: required.has(placeholder.token),
                    }));
                }
                return normalized;
            },
            appInitials(name) {
                return (name || '?')
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join('')
                    .toUpperCase();
            },
            setWorkingCopy(application) {
                this.selectedApp = application;
                const working = {};
                const original = {};

                for (const field of TEMPLATE_FIELDS) {
                    working[field] = application[field] || '';
                    original[field] = application[field] || '';
                }

                this.workingTemplates = working;
                this.originalTemplates = original;
            },
            selectApp(application) {
                if (this.selectedApp && application.id === this.selectedApp.id) {
                    return;
                }

                if (this.isDirty) {
                    this.requestDiscardConfirm(() => this.discardAndSelect(application));
                    return;
                }

                this.discardAndSelect(application);
            },
            discardAndSelect(application) {
                if (this.selectedApp) {
                    this.$set(this.dirtyByAppId, this.selectedApp.id, false);
                }
                this.setWorkingCopy(application);
            },
            setChannel(groupKey, channel) {
                this.activeChannel = { ...this.activeChannel, [groupKey]: channel };
            },
            isFieldDirty(field) {
                return (this.workingTemplates[field] || '') !== (this.originalTemplates[field] || '');
            },
            isFieldConfigured(field) {
                return Boolean((this.originalTemplates[field] || '').trim());
            },
            requestDiscardConfirm(onConfirm, onCancel = null) {
                this.pendingDiscard = { confirm: onConfirm, cancel: onCancel };
                this.discardModalOpen = true;
            },
            handleDiscardConfirm() {
                this.discardModalOpen = false;
                const pending = this.pendingDiscard;
                this.pendingDiscard = null;
                if (pending && pending.confirm) {
                    pending.confirm();
                }
            },
            handleDiscardCancel() {
                this.discardModalOpen = false;
                const pending = this.pendingDiscard;
                this.pendingDiscard = null;
                if (pending && pending.cancel) {
                    pending.cancel();
                }
            },
            handleApplicationCreated(application) {
                this.showAddModal = false;
                if (!application) {
                    // Defensive: unexpected response shape — refetch to stay consistent
                    this.loading = true;
                    this.loadApplications();
                    return;
                }
                this.applications.push(application);
                this.$toasted.success(`Notification templates added for "${application.name}".`);
                this.selectApp(application);
            },
            handleSaveShortcut(event) {
                if ((event.metaKey || event.ctrlKey) && event.key === 's') {
                    event.preventDefault();
                    this.save();
                }
            },
            handleBeforeUnload(event) {
                if (this.isDirty) {
                    event.preventDefault();
                    event.returnValue = '';
                }
            },
            save() {
                // Opens the confirm modal; doSave() performs the PUT (Ctrl+S lands here too)
                if (!this.isDirty || this.hasBlockingErrors || this.saving) {
                    return;
                }
                if (this.showAddModal || this.discardModalOpen) {
                    return;
                }
                this.saveModalOpen = true;
            },
            async doSave() {
                this.saveModalOpen = false;
                if (!this.isDirty || this.hasBlockingErrors || this.saving) {
                    return;
                }

                const changedFields = this.dirtyFields;
                const payload = {};
                for (const field of changedFields) {
                    payload[field] = this.workingTemplates[field];
                }

                this.saving = true;
                try {
                    await this.$api.put(applicationsApi.updateNotificationTemplates(this.selectedApp.id), payload);

                    // Sync local state so dirty tracking resets
                    for (const field of changedFields) {
                        this.selectedApp[field] = this.workingTemplates[field];
                        this.originalTemplates[field] = this.workingTemplates[field];
                    }
                    this.$set(this.dirtyByAppId, this.selectedApp.id, false);
                    this.$toasted.success(`Templates updated for ${this.selectedApp.name}.`);
                } catch (err) {
                    const message = err?.response?.data?.message;
                    this.$toasted.error(message || 'Failed to save templates. Please try again later.');
                } finally {
                    this.saving = false;
                }
            },
        },
        async created() {
            await this.loadApplications();
        },
        mounted() {
            window.addEventListener('keydown', this.handleSaveShortcut);
            window.addEventListener('beforeunload', this.handleBeforeUnload);
        },
        beforeDestroy() {
            window.removeEventListener('keydown', this.handleSaveShortcut);
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
        },
        beforeRouteLeave(to, from, next) {
            if (!this.isDirty) {
                return next();
            }
            this.requestDiscardConfirm(
                () => next(),
                () => next(false)
            );
        },
    };
</script>

<style scoped>
    .bg-color {
        background-color: rgba(255, 255, 255, 0.9);
    }

    .page-heading {
        margin: 0;
    }

    .loading-panel,
    .empty-state {
        border-radius: 0 0 27px 27px;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 400px;
        text-align: center;
        color: #5b6b7b;
        padding: 40px;
    }

    .empty-state-icon {
        font-size: 52px;
        color: #b9c6d6;
    }

    .empty-state h4 {
        margin: 0;
    }

    .empty-state p {
        margin: 0;
        font-size: 0.85rem;
        color: #7a8a9a;
    }

    .content-grid {
        display: grid;
        grid-template-columns: 250px 1fr;
        min-height: 500px;
        border-radius: 0 0 27px 27px;
        overflow: hidden;
    }

    @media (max-width: 991px) {
        .content-grid {
            grid-template-columns: 1fr;
        }
    }

    .app-rail {
        border-right: 1px solid #e8eef5;
        background: #f8fafd;
        padding: 20px 12px;
    }

    @media (max-width: 991px) {
        .app-rail {
            border-right: none;
            border-bottom: 1px solid #e8eef5;
        }

        .rail-list {
            display: flex;
            overflow-x: auto;
            gap: 6px;
            padding-bottom: 4px;
        }

        .app-rail-item {
            flex: 0 0 auto;
            width: auto;
        }
    }

    .rail-heading {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #7a8a9a;
        padding: 0 10px 12px;
    }

    .rail-count {
        background: #e4e9f2;
        color: #5b6b7b;
        font-size: 0.7rem;
        font-weight: 700;
        border-radius: 10px;
        padding: 1px 8px;
    }

    .app-rail-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        text-align: left;
        background: transparent;
        border: none;
        border-radius: 5px;
        padding: 9px 10px;
        /* Global styles.css sets `button + button { margin-left: 15px }` — neutralize it */
        margin: 0 0 2px;
        font-size: 0.88rem;
        color: #34495e;
        cursor: pointer;
        transition: background 0.12s ease;
    }

    .app-rail-item:hover {
        background: #eef1fb;
    }

    .app-rail-item.active {
        background: rgba(var(--base-theme-color-rgb), 0.12);
        font-weight: 600;
        color: var(--base-theme-color);
    }

    .app-rail-avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 28px;
        height: 28px;
        border-radius: 5px;
        background: #e4e9f2;
        color: #5b6b7b;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.02em;
    }

    .app-rail-item.active .app-rail-avatar {
        background: var(--base-theme-color);
        color: #ffffff;
    }

    .app-rail-name {
        flex: 1;
        min-width: 0;
        text-align: left;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .rail-add-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        width: 100%;
        margin: 10px 0 0;
        padding: 9px 10px;
        border: 1px dashed #b9c6d6;
        border-radius: 5px;
        background: transparent;
        color: #5b6b7b;
        font-size: 0.82rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.12s ease, border-color 0.12s ease;
    }

    .rail-add-btn:hover {
        background: #eef1fb;
        border-color: var(--base-theme-color);
        color: var(--base-theme-color);
    }

    .rail-add-icon {
        font-size: 17px;
    }

    .empty-add-btn {
        width: auto;
        padding: 10px 24px;
        margin-top: 14px;
    }

    .unset-chip {
        flex: 0 0 auto;
        font-size: 0.62rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 1px 7px;
        border-radius: 8px;
        background: #fdf6e7;
        color: #8a6116;
        border: 1px solid #f0dcae;
    }

    .channel-tab.active .unset-chip {
        background: rgba(255, 255, 255, 0.18);
        color: #ffffff;
        border-color: rgba(255, 255, 255, 0.35);
    }

    .template-panel {
        padding: 24px 32px 32px;
        min-width: 0;
        background: #fbfcfe;
    }

    .panel-intro {
        margin-bottom: 20px;
    }

    .app-title {
        font-size: 1.3rem;
        margin: 0 0 6px;
    }

    .app-subtitle {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.82rem;
        color: #7a8a9a;
        margin: 0;
    }

    .subtitle-icon {
        font-size: 16px;
        color: #9aa9b8;
    }

    .flow-card {
        background: #ffffff;
        border: 1px solid #e8eef5;
        border-radius: 8px;
        padding: 20px 24px 24px;
        margin-bottom: 20px;
        box-shadow: 0 1px 3px rgba(45, 62, 80, 0.05);
    }

    .flow-header {
        display: flex;
        align-items: baseline;
        gap: 12px;
        flex-wrap: wrap;
        margin-bottom: 14px;
    }

    .flow-title {
        font-size: 1.05rem;
        margin: 0;
    }

    .flow-description {
        font-size: 0.78rem;
        color: #7a8a9a;
    }

    /* Top-rounded tabs matching the app's page-tab / top-menu pattern */
    .channel-tabs {
        display: flex;
        gap: 4px;
        border-bottom: 2px solid var(--base-theme-tab-color);
        margin-bottom: 16px;
    }

    .channel-tab {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        background: rgb(241, 247, 255);
        color: #5155c3;
        font-family: 'Roboto', 'Open Sans', sans-serif;
        font-size: 0.82rem;
        font-weight: 500;
        padding: 8px 20px;
        border-radius: 10px 10px 0 0;
        /* Global styles.css sets `button + button { margin-left: 15px }` — neutralize it */
        margin: 0;
        cursor: pointer;
        transition: background 0.12s ease, color 0.12s ease;
    }

    .channel-tab-icon {
        font-size: 16px;
    }

    .channel-tab:hover:not(.active) {
        background: #e4eefc;
    }

    .channel-tab.active {
        background: var(--base-theme-tab-color);
        color: #ffffff;
    }

    .channel-tab.active .dirty-dot {
        background: #ffc95c;
    }

    .dirty-dot {
        flex: 0 0 auto;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #e3910b;
    }

    .save-row {
        gap: 16px;
        margin-top: 4px;
    }

    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 0.78rem;
        font-weight: 600;
        padding: 5px 12px;
        border-radius: 5px;
        white-space: nowrap;
    }

    .status-pill-icon {
        font-size: 15px;
    }

    .status-pill-warning {
        background: #fdf6e7;
        color: #8a6116;
        border: 1px solid #f0dcae;
    }

    .status-pill-error {
        background: #fdecea;
        color: #a03024;
        border: 1px solid #f2c4bf;
    }

    /* Matches the app-wide .save-btn convention (ClientNotificationsSetting, application builder) */
    .save-btn {
        background: #5057c3;
        box-shadow: 0px 8px 22px 6px rgba(0, 0, 0, 0.24), inset 0px 4px 4px rgba(220, 220, 220, 0.25);
        color: white;
        width: 150px;
        height: 50px;
        font-family: Roboto;
        font-size: 16px;
        border-radius: 5px;
        border: none;
        cursor: pointer;
        /* Global styles.css sets `button + button { margin-left: 15px }` — neutralize it */
        margin: 0;
    }

    .save-btn.btn-disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }
</style>
